
import { PayrollEmployeeH3List, PaymentApiPayrollemployeeh3ListRequest } from '@/api/openapi';
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { paginationMixin, paginationPersistenceMixin, parsePagination } from '@/utils';
import PayrollEmployeeFilters from './PayrollEmployeeFilters.vue';
import Pagination from '../Pagination.vue';

// @ts-ignore
@Component({
  components: { PayrollEmployeeFilters, Pagination },
  mixins: [paginationMixin, paginationPersistenceMixin],
})
export default class PayrollH3 extends Vue {
  @Action displayError!: (s: string) => void;
  @Action displaySuccess!: (s: string) => void;
  @Getter loggedInUserHasAnyPermission: any;

  // Declare mixin properties and methods for TypeScript
  currentPage!: number;
  currentLimit!: number;
  loadPaginationOptions!: () => void;
  savePaginationOptions!: () => void;
  changePageWithPersistence!: (page: number) => void;
  changeLimitWithPersistence!: (limit: number) => void;
  getGlobalIndex!: (localIndex: number, pagination: any) => number;

  // Set the pagination key
  paginationKey = 'payrollEmployeesH3New';

  data: Array<PayrollEmployeeH3List & { isChecked: boolean; isSubmitting: boolean; isCreated: boolean; isError: boolean }> | null = null;
  loading = false;
  hasSearched = false;
  submitting = false;
  pagination: any = null;

  search: PaymentApiPayrollemployeeh3ListRequest = {
    placementGroup: undefined,
    period: undefined,
    periodPart: undefined,
    workplace: undefined,
    workplaceType: undefined,
  };

  existsFilter: string | null = null;

  // @Watch('search', { deep: true })
  // onSearchChanged(newSearch: any, oldSearch: any) {
  //   // Check if any search parameter has changed
  //   const filtersExceptExists = ['placementGroup', 'period', 'periodPart', 'workplace', 'workplaceType'];
  //   const shouldRefetch = filtersExceptExists.some(filter => newSearch[filter] !== oldSearch[filter]);

  //   if (shouldRefetch) {
  //     this.currentPage = 1; // Reset to first page when search changes
  //     this.savePaginationOptions();
  //     this.loadData(this.currentPage, this.currentLimit);
  //   }
  // }

  searchData() {
    this.hasSearched = true;
    this.currentPage = 1; // Reset to first page when search is performed
    this.savePaginationOptions();
    this.loadData(this.currentPage, this.currentLimit);
  }

  get filteredData() {
    if (!this.data) return [];

    if (!this.existsFilter) return this.data;

    return this.data.filter(emp => {
      switch (this.existsFilter) {
        case 'true':
          return emp.existsInH3;
        case 'false':
          return !emp.existsInH3;
        case 'sent-yes':
          return emp.dateSentToH3;
        case 'sent-no':
          return !emp.dateSentToH3;
        default:
          return true;
      }
    });
  }

  get allSelected() {
    return (this.filteredData || []).every(a => a.isChecked);
  }

  get someSelected() {
    return (this.filteredData || []).some(a => a.isChecked);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async loadData(page?: number, limit?: number) {
    // Use current pagination state if not provided
    // const currentPage = page || this.currentPage;
    // const currentLimit = limit || this.currentLimit;
    // const currentPage = undefined;
    // const currentLimit = undefined;

    this.loading = true;

    try {
      const response = await this.$paymentApi.payrollemployeeh3List({
        period: this.search.period ? this.search.period : undefined,
        periodPart: this.search.periodPart ? this.search.periodPart : undefined,
        placementGroup: this.search.placementGroup ? this.search.placementGroup : undefined,
        workplace: this.search.workplace ? this.search.workplace : undefined,
        workplaceType: this.search.workplaceType ? this.search.workplaceType : undefined,
        exists: this.search.exists ? this.search.exists : undefined,
      });

      this.data = response.data.map(d => ({ isChecked: false, isSubmitting: false, isCreated: false, isError: false, ...d }));
      this.pagination = parsePagination(response);
    } catch (e) {
      this.data = [];
      this.$log.error(e);
      this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
    } finally {
      this.loading = false;
    }
  }

  apiErrorMessage(e: any): string {
    if (e?.response?.status === 400) return 'Gögnin eru ekki í samræmi við H3. Vinsamlegast yfirfarið stillingar á launaflokkum.';
    return 'Óvænt villa kom upp. Vinsamlegast reyndu aftur.';
  }

  async createInH3(emp: PayrollEmployeeH3List) {
    if (this.search.period) {
      this.submitting = true;

      try {
        await this.$paymentApi.createInH3({
          period: this.search.period,
          person: emp.id,
        });

        this.displaySuccess(`Starfsmaðurinn „${emp.name}” hefur verið stofnaður.`);

        // Update local state instead of refetching
        if (this.data) {
          const index = this.data.findIndex(d => d.id === emp.id);
          if (index !== -1) {
            this.data[index].existsInH3 = true;
            this.data[index].dateSentToH3 = new Date().toISOString();
          }
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(this.apiErrorMessage(e));
      } finally {
        this.submitting = false;
      }
    }
  }

  async rehireInH3(emp: PayrollEmployeeH3List) {
    if (this.search.period) {
      this.submitting = true;

      try {
        await this.$paymentApi.rehireInH3({
          period: this.search.period,
          person: emp.id,
        });

        this.displaySuccess(`Starfsmaðurinn „${emp.name}” hefur verið endurráðin/n.`);

        // Update local state instead of refetching
        if (this.data) {
          const index = this.data.findIndex(d => d.id === emp.id);
          if (index !== -1) {
            this.data[index].existsInH3 = true;
            this.data[index].reusableInH3 = false;
            this.data[index].dateSentToH3 = new Date().toISOString();
          }
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(this.apiErrorMessage(e));
      } finally {
        this.submitting = false;
      }
    }
  }

  async runSelected() {
    if (this.data && this.search.period) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i !== this.data.length; i++) {
        const d = this.data[i];
        if (d.isChecked && !d.isCreated && !d.isError) {
          this.data[i].isSubmitting = true;
          try {
            if (d.reusableInH3) {
              // eslint-disable-next-line no-await-in-loop
              await this.$paymentApi.rehireInH3({
                period: this.search.period,
                person: d.id,
              });
            } else {
              // eslint-disable-next-line no-await-in-loop
              await this.$paymentApi.createInH3({
                period: this.search.period,
                person: d.id,
              });
            }
            this.data[i].isCreated = true;
          } catch {
            this.data[i].isError = true;
          } finally {
            this.data[i].isChecked = false;
            this.data[i].isSubmitting = false;
          }
        }
      }
    }
  }

  selectAll() {
    if (this.data) {
      this.$set(
        this,
        'data',
        this.data.map(a => ({
          ...a,
          isChecked: !this.allSelected,
        })),
      );

      // this.event.data.attendees[index].isCreated = value;
    }
  }

  rowChecked(index: number, value: boolean) {
    if (this.data) {
      this.data[index].isChecked = value;
    }
  }

  created() {
    this.loadPaginationOptions();
  }
}
